import { startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import { graphql, Link, navigate, useStaticQuery } from 'gatsby';
import React, { useEffect, useState, useRef } from 'react';
import { Event } from '../../components/Event';
import EventList from '../../components/EventList';
import Layout from '../../components/Layout';
import SectionHeader from '../../components/SectionHeader';
import SEO from '../../components/Seo';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import ContentContainer from '../../components/common/ContentContainer';
import { FormField, FormWrapper } from '../../components/common/Form';
import Selection from '../../components/common/Selection';
import { groupEventsByMonth } from '../../util';
import { transformGradeString } from '../../util/grades';

enum EventQueryParamsFilter {
  search = 'search',
  grades = 'grades',
  location = 'location',
  audience = 'audience',
  institute = 'institute',
  oneDayConference = 'oneDayConference',
  courses = 'courses'
}

const EventQueryParamsFilterArray = [
  EventQueryParamsFilter.search,
  EventQueryParamsFilter.grades,
  EventQueryParamsFilter.location,
  EventQueryParamsFilter.audience,
  EventQueryParamsFilter.institute,
  EventQueryParamsFilter.oneDayConference,
  EventQueryParamsFilter.courses
];

const EventQueryParamsFilterDefault = {
  [EventQueryParamsFilter.institute]: 'true',
  [EventQueryParamsFilter.oneDayConference]: 'true',
  [EventQueryParamsFilter.courses]: 'true',
  [EventQueryParamsFilter.location]: 'all',
  [EventQueryParamsFilter.audience]: 'all'
};

export enum EventTypes {
  INSTITUTE = 'Institute',
  ONE_DAY_CONFERENCE = 'One-Day Conference',
  SEMINAR_SERIES = 'Seminar Series',
  COURSES = 'Courses'
}

const Events: React.FC = () => {
  const updateQueryParams = (params: URLSearchParams) => {
    navigate(`?${params.toString()}`, { replace: true });
  };

  const locationOptions = [
    { label: 'All', value: 'all' },
    { label: 'Remote', value: 'remote' },
    { label: 'In-Person', value: 'in-person' }
  ];

  const audienceOptions = [
    { label: 'All', value: 'all' },
    { label: 'Teachers', value: 'Teachers' },
    { label: 'Families', value: 'Families' }
  ];

  const eventTypeOptions = [
    { label: 'Institute', value: 'institute' },
    { label: 'One-Day Conference', value: 'oneDayConference' },
    { label: 'Courses', value: 'courses' }
  ];

  const updateFilter = (data: any) => {
    const params = new URLSearchParams();

    Object.keys(data).forEach(key => {
      if (EventQueryParamsFilterArray.some(p => p === key)) {
        if (key === 'grades' && data[key]?.length) {
          params.set(key, data[key]);
        } else if (data[key]) {
          params.set(key, data[key]);
        }
      }
    });
    updateQueryParams(params);
  };

  const formik = useFormik<any>({
    initialValues: {},
    onSubmit: () => {}
  });

  const mapQueryParamsToForm = (params: URLSearchParams) => {
    const form: any = {};
    params.forEach((value, key) => {
      if (
        [
          EventQueryParamsFilter.institute,
          EventQueryParamsFilter.oneDayConference,
          EventQueryParamsFilter.courses
        ].includes(key as EventQueryParamsFilter)
      ) {
        form[key] = value === 'true';
      } else if (key === EventQueryParamsFilter.grades) {
        form[key] = value ? value.split(',') : [];
      } else {
        form[key] = value;
      }
    });
    return form;
  };

  const resetFiltersToDefault = () => {
    formik.setValues({
      search: '',
      grades: [],
      location: locationOptions[0].value,
      audience: audienceOptions[0].value,
      institute: true,
      oneDayConference: true,
      courses: true,
      eventTypes: ['institute', 'oneDayConference', 'courses']
    });
  };

  // Sync eventTypes array with boolean values (but don't override if user just changed dropdown)
  useEffect(() => {
    // Only sync if eventTypes doesn't exist or is empty (initial load)
    if (!formik.values.eventTypes || formik.values.eventTypes.length === 0) {
      const types: string[] = [];
      if (formik.values.institute) types.push('institute');
      if (formik.values.oneDayConference) types.push('oneDayConference');
      if (formik.values.courses) types.push('courses');
      formik.setFieldValue('eventTypes', types, false);
    }
  }, [
    formik.values.institute,
    formik.values.oneDayConference,
    formik.values.courses
  ]);

  // Handle event types dropdown change
  const handleEventTypesChange = (selected: any) => {
    const selectedValues = selected ? selected.map((s: any) => s.value) : [];
    formik.setFieldValue('eventTypes', selectedValues);
    formik.setFieldValue('institute', selectedValues.includes('institute'));
    formik.setFieldValue(
      'oneDayConference',
      selectedValues.includes('oneDayConference')
    );
    formik.setFieldValue('courses', selectedValues.includes('courses'));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (EventQueryParamsFilterArray.some(p => params.has(p))) {
      formik.setValues(mapQueryParamsToForm(params));
    } else {
      resetFiltersToDefault();
    }
  }, []);

  const [totalEvents, setTotalEvents] = useState(0);
  const [gradeOptions, setGradeOptions] = useState([
    { label: 'All', value: 'all' },
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '10', value: '10' },
    { label: '11', value: '11' },
    { label: '12', value: '12' }
  ]);
  const [formattedEvents, setFormattedEvents] = useState<any[]>([]);
  const hasScrolledToFirstMonth = useRef(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [sidebarTop, setSidebarTop] = useState(96); // Default to top-24

  const data = useStaticQuery(graphql`
    {
      aboutEvents: file(relativePath: { eq: "images/events-1.jpg" }) {
        childImageSharp {
          sizes(maxWidth: 600) {
            ...GatsbyImageSharpSizes
          }
        }
      }
      allContentfulEvent(
        sort: { fields: startDate, order: ASC }
        filter: { node_locale: { eq: "en-US" } }
      ) {
        edges {
          node {
            title
            type
            startDate
            grades
            location
            endDate
            description {
              description
            }
            slug
            price
          }
        }
      }
    }
  `);

  const getEventSectionSetting = (section: any) => {
    const location = section?.location?.name?.toLowerCase();
    return location?.includes('via zoom') || location?.includes('remote')
      ? 'remote'
      : 'in-person';
  };

  const getEventType = (data: any, isContentful = false) => {
    if (isContentful) {
      switch (data.type) {
        case 'Saturday Reunion':
        case 'Seminar Series':
          return EventTypes.SEMINAR_SERIES;
        case 'Institute':
          return EventTypes.INSTITUTE;
        case 'One-Day Conference':
          return EventTypes.ONE_DAY_CONFERENCE;
        case 'Courses':
          return EventTypes.COURSES;
        default:
          '';
      }
    } else {
      const eventType = data?.group?.eventPath?.event?.eventType?.name;
      switch (eventType) {
        case 'Series':
        case 'Seminar':
          return EventTypes.SEMINAR_SERIES;
        case 'Institute':
          return EventTypes.INSTITUTE;
        case 'Conference Day':
          return EventTypes.ONE_DAY_CONFERENCE;
        default:
          '';
      }
    }
  };

  const getAllEvents = () => {
    return [
      ...data.allContentfulEvent.edges
        .map((event: any) => event.node)
        .map((event: Event) => {
          return {
            ...event,
            type: 'EVENT',
            setting:
              event?.location?.toLowerCase().includes('via zoom') ||
              event?.location?.toLowerCase().includes('remote')
                ? 'remote'
                : 'in-person',
            eventType: getEventType(event, true),
            grades: transformGradeString(event.grades),
            startDate: new Date(event.startDate),
            endDate: new Date(event.endDate),
            desc: event.description?.description
          };
        })
    ].filter((e: any) => new Date(e.endDate) >= startOfDay(new Date()));
  };

  useEffect(() => {
    // @ts-ignore
    setFormattedEvents(getAllEvents());
    setTotalEvents(getAllEvents().length);
  }, [data]);

  useEffect(() => {
    let events = getAllEvents();
    updateFilter(formik.values);
    const {
      search,
      grades,
      location,
      audience,
      institute,
      oneDayConference,
      courses
    } = formik.values;
    if (search) {
      events = events.filter(
        event =>
          event.title.toLowerCase().includes(search.toLowerCase()) ||
          event.desc.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (grades?.length) {
      events = events.filter(event =>
        grades.some((g: string) => event.grades.includes(g))
      );
    }
    if (location !== 'all') {
      events = events.filter(event => event.setting === location);
    }
    if (audience && audience !== 'all') {
      // Filter by audience - assuming events have an audience field
      // For now, this will work once the audience field is added to events
      events = events.filter((event: any) => {
        // Check if event has audience field and matches
        return (
          event.audience === audience ||
          (event.audience &&
            Array.isArray(event.audience) &&
            event.audience.includes(audience))
        );
      });
    }
    const eventTypesToInclude = [
      (institute && EventTypes.INSTITUTE) || '',
      (oneDayConference && EventTypes.ONE_DAY_CONFERENCE) || '',
      (courses && EventTypes.COURSES) || ''
    ];
    events = events.filter(event =>
      eventTypesToInclude.includes(event.eventType)
    );

    setFormattedEvents(events);
  }, [formik.values, data]);

  const isFilterApplied = () => {
    if (
      formik.values.search ||
      formik.values.grades?.length ||
      formik.values.location !== 'all' ||
      (formik.values.audience && formik.values.audience !== 'all')
    ) {
      if (
        !formik.values.institute &&
        !formik.values.oneDayConference &&
        !formik.values.courses
      ) {
        return false;
      }
      return true;
    }
  };

  // School year months: September to August
  const schoolYearMonths = [
    'September',
    'October',
    'November',
    'December',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August'
  ];

  // Get months that have events
  const eventsByMonth = groupEventsByMonth(formattedEvents);
  const monthsWithEvents = eventsByMonth.map(em => em.month);
  const availableMonths = schoolYearMonths.filter(month =>
    monthsWithEvents.includes(month)
  );

  const scrollToMonth = (month: string) => {
    const element = document.getElementById(`month-${month}`);
    if (element) {
      const mainNavbar = document.querySelector('nav[role="navigation"]');
      const navbarHeight = mainNavbar
        ? mainNavbar.getBoundingClientRect().height
        : 0;
      const totalOffset = navbarHeight;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - totalOffset - 20;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Calculate sidebar top offset based on header position
  useEffect(() => {
    const calculateSidebarTop = () => {
      if (headerRef.current) {
        const navbar = document.querySelector('nav[role="navigation"]');
        const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
        // Get header bottom position relative to viewport top (when at scroll position 0)
        const headerRect = headerRef.current.getBoundingClientRect();
        // The sidebar should stick when header bottom reaches just below navbar
        // So top = header bottom position - navbar height + spacing
        const stickyTop = headerRect.bottom - navbarHeight + 16; // 16px spacing
        setSidebarTop(Math.max(stickyTop, navbarHeight + 16)); // Ensure it's at least below navbar
      }
    };

    // Calculate on mount and resize (wait for layout)
    const timer = setTimeout(calculateSidebarTop, 100);
    window.addEventListener('resize', calculateSidebarTop);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateSidebarTop);
    };
  }, []);

  // Scroll to first month with upcoming events on initial load
  useEffect(() => {
    if (formattedEvents.length > 0 && !hasScrolledToFirstMonth.current) {
      const today = startOfDay(new Date());

      // Find events with start dates in the future
      const upcomingEvents = formattedEvents.filter(
        (event: any) => new Date(event.startDate) >= today
      );

      if (upcomingEvents.length > 0) {
        // Group upcoming events by month
        const upcomingEventsByMonth = groupEventsByMonth(upcomingEvents);

        // Find the first month in school year order that has upcoming events
        const firstUpcomingMonth = schoolYearMonths.find(month =>
          upcomingEventsByMonth.some(em => em.month === month)
        );

        if (firstUpcomingMonth) {
          // Wait a bit for the DOM to render
          setTimeout(() => {
            scrollToMonth(firstUpcomingMonth);
            hasScrolledToFirstMonth.current = true;
          }, 100);
        }
      }
    }
  }, [formattedEvents]);

  return (
    <Layout>
      <SEO title="Events" />
      <ContentContainer>
        <div ref={headerRef}>
          <SectionHeader header="Events" />
          <div className="mt-4">
            <p className="text-primary">
              <Link to="/events/faq" className="hover:underline">
                Event FAQs
              </Link>
            </p>
          </div>
        </div>

        {/* Mobile Filters - Collapsible */}
        <div className="lg:hidden mt-10 mb-6">
          <details className="bg-white shadow-md rounded-lg">
            <summary className="p-4 cursor-pointer font-semibold text-primary">
              Filter Events
            </summary>
            <div className="p-4 border-t">
              <FormWrapper formik={formik} hideActionButtons={true}>
                <div className="space-y-4 mb-4">
                  <FormField
                    name="search"
                    type="text"
                    className="w-full"
                    placeholder="Search Events"
                    label="Search Events"
                  />
                  <FormField
                    name="grades"
                    className="w-full"
                    type="select"
                    isMulti
                    options={gradeOptions}
                  />
                  <FormField
                    name="location"
                    className="w-full"
                    type="select"
                    options={locationOptions}
                  />
                </div>

                <div className="space-y-2 mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Audience
                  </h4>
                  {audienceOptions.map((option, i: number) => (
                    <div key={i}>
                      <Selection
                        item={{
                          label: option.label,
                          selected: formik.values.audience === option.value
                        }}
                        setSelected={() => {
                          formik.setFieldValue('audience', option.value);
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <FormField
                    name="eventTypes"
                    className="w-full"
                    type="select"
                    isMulti
                    options={eventTypeOptions}
                    onChange={handleEventTypesChange}
                  />
                </div>

                <div className="mb-4">
                  <Button
                    text="Reset filters"
                    type="button"
                    size="sm"
                    color="cancel"
                    onClick={() => resetFiltersToDefault()}
                  />
                </div>

                {isFilterApplied() && (
                  <div className="mb-4 sans-serif text-sm text-gray-600">
                    Showing {formattedEvents?.length} of {totalEvents} results{' '}
                    {formik.values.search && `for "${formik.values.search}"`}
                  </div>
                )}

                {availableMonths.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Scroll to month
                    </h4>
                    <div className="overflow-x-auto">
                      <div className="flex gap-2 min-w-max">
                        {schoolYearMonths.map(month => {
                          const hasEvents = availableMonths.includes(month);
                          return (
                            <button
                              key={month}
                              onClick={() => scrollToMonth(month)}
                              disabled={!hasEvents}
                              className={`px-4 py-2 rounded-md font-medium text-sm transition-all whitespace-nowrap border-2 border-primary text-primary hover:bg-primary hover:text-white hover:shadow-md ${
                                hasEvents
                                  ? 'cursor-pointer'
                                  : 'opacity-50 cursor-not-allowed'
                              }`}
                            >
                              {month.substring(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </FormWrapper>
            </div>
          </details>
        </div>

        <div className="flex gap-6 mt-10">
          {/* Sticky Sidebar with Filters */}
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <div
              className="sticky bg-white shadow-md rounded-lg p-4"
              style={{ top: `${sidebarTop}px` }}
            >
              <FormWrapper formik={formik} hideActionButtons={true}>
                <h3 className="text-lg font-semibold text-primary mb-4">
                  Filter Events
                </h3>

                <div className="space-y-4 mb-4">
                  <FormField
                    name="search"
                    type="text"
                    className="w-full"
                    placeholder="Search Events"
                    label="Search Events"
                  />
                  <FormField
                    name="grades"
                    className="w-full"
                    type="select"
                    isMulti
                    options={gradeOptions}
                  />
                  <FormField
                    name="location"
                    className="w-full"
                    type="select"
                    options={locationOptions}
                  />
                </div>

                <div className="space-y-2 mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Audience
                  </h4>
                  {audienceOptions.map((option, i: number) => (
                    <div key={i}>
                      <Selection
                        item={{
                          label: option.label,
                          selected: formik.values.audience === option.value
                        }}
                        setSelected={() => {
                          formik.setFieldValue('audience', option.value);
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <FormField
                    name="eventTypes"
                    className="w-full"
                    type="select"
                    isMulti
                    options={eventTypeOptions}
                    onChange={handleEventTypesChange}
                  />
                </div>

                <div className="mb-4">
                  <Button
                    text="Reset filters"
                    type="button"
                    size="sm"
                    color="cancel"
                    onClick={() => resetFiltersToDefault()}
                  />
                </div>

                {isFilterApplied() && (
                  <div className="mb-4 sans-serif text-sm text-gray-600">
                    Showing {formattedEvents?.length} of {totalEvents} results{' '}
                    {formik.values.search && `for "${formik.values.search}"`}
                  </div>
                )}

                {availableMonths.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Scroll to month
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {schoolYearMonths.map(month => {
                        const hasEvents = availableMonths.includes(month);
                        return (
                          <button
                            key={month}
                            onClick={() => scrollToMonth(month)}
                            disabled={!hasEvents}
                            className={`px-3 py-1.5 rounded-md font-medium text-xs transition-all border-2 border-primary text-primary hover:bg-primary hover:text-white hover:shadow-md ${
                              hasEvents
                                ? 'cursor-pointer'
                                : 'opacity-50 cursor-not-allowed'
                            }`}
                          >
                            {month.substring(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </FormWrapper>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <Card>
              {formattedEvents.length ? (
                <EventList events={formattedEvents} />
              ) : (
                <div className="text-center text-gray-500">No events found</div>
              )}
            </Card>
          </div>
        </div>
      </ContentContainer>
    </Layout>
  );
};

export default Events;
